import React, { useState } from "react"
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQueryClient } from "react-query"
import Button from "components/Buttons/Button"
import AuthenticationWrapper from "pages/Unauthenticated/AuthenticationWrapper"
import { signOut } from "services/authentication"
import { resendConfirmationInstructions } from "api/sessions"
import { errorToast, successToast } from "utilities/toasts"
import { User } from "sharedTypes"
import { useIntercom } from "react-use-intercom"

type Props = {
  currentUser: User
}

const ConfirmEmail = ({ currentUser }: Props) => {
  const queryClient = useQueryClient()
  const { shutdown: shutdownIntercom } = useIntercom()
  const [resending, setResending] = useState(false)

  if (!currentUser.id) return null

  const handleResend = () => {
    if (resending) return
    setResending(true)
    resendConfirmationInstructions(currentUser.email)
      .then(() => successToast({ title: `Instructions sent to ${currentUser.email}` }))
      .catch(() => errorToast())
      .finally(() => setResending(false))
  }

  return (
    <AuthenticationWrapper
      pageTitle="Verify your email"
      header={(
        <Button
          size="sm"
          variant="outline"
          colorScheme="button.light"
          onClick={() => signOut(queryClient, shutdownIntercom)}
        >
          Sign Out
        </Button>
      )}
      body={(
        <VStack w="full" spacing={6} align="stretch">
          <Text>
            Your email address must be verified before you can access Diobox.
          </Text>
          <Text>
            We sent a verification link to your email. Click the link to confirm your address.
            If you don&apos;t see the message, check your spam folder or
            resend the verification email below.
          </Text>

          <FormControl>
            <FormLabel>Email address</FormLabel>
            <Input value={currentUser.email} isReadOnly bg="gray.50" color="gray.600" />
          </FormControl>

          <Box>
            <Button
              colorScheme="button.primary"
              isLoading={resending}
              isDisabled={!currentUser.email}
              onClick={handleResend}
            >
              Resend Verification Email
            </Button>
          </Box>
        </VStack>
      )}
    />
  )
}

export default ConfirmEmail
